import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isAdminAllowed } from '@/lib/adminAllowlist';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * SystemAdminLink — sidebar entry that takes an allowlisted user from
 * MRP to the Admin app via a Gateway-minted handoff JWT.
 *
 * Architecture: same-origin POST to MRP's `/api/admin/handoff`. MRP
 * holds the Gateway shared secret server-side and calls
 * `POST /api/sso/mint` with `Authorization: ServiceToken <secret>`.
 * The frontend never sees the secret and never talks to the Gateway
 * directly — defence in depth + zero CORS.
 *
 * UI gate only. A non-allowlisted user who calls /api/admin/handoff
 * directly via devtools is rejected by MRP backend AND by the Gateway.
 */
export default function SystemAdminLink() {
  const { currentUser } = useAuth();
  const [inFlight, setInFlight] = useState(false);

  // Hide the entry entirely for non-admins — never even render the
  // button. The Gateway allowlist is the source of truth; this list
  // is just a UI shortcut.
  if (!isAdminAllowed(currentUser?.clerk_user_id)) {
    return null;
  }

  const handleClick = async () => {
    if (inFlight) return;
    setInFlight(true);
    let response;
    try {
      response = await fetch('/api/admin/handoff', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch {
      // Network failure — fetch threw. Generic, opaque toast.
      // Do NOT log the error object verbatim (may include URL/headers).
      console.error('[SystemAdminLink] handoff failed (network)');
      toast.error('Could not open System Admin. Please try again.');
      setInFlight(false);
      return;
    }

    if (response.ok) {
      let exchangeUrl;
      try {
        const body = await response.json();
        exchangeUrl = body && typeof body.exchange_url === 'string' ? body.exchange_url : null;
      } catch {
        exchangeUrl = null;
      }
      if (!exchangeUrl) {
        console.error('[SystemAdminLink] handoff returned malformed body');
        toast.error('Could not open System Admin. Please try again.');
        setInFlight(false);
        return;
      }
      // Hard-navigate — the browser will pick up the new
      // `nombase_session` cookie issued by Admin's /api/sso receiver.
      // Do NOT re-enable inFlight; the page is leaving.
      window.location.assign(exchangeUrl);
      return;
    }

    if (response.status === 401) {
      toast.error('Session expired, please sign in again.');
      // Bounce back through the Gateway — MRP's existing 401 handling
      // also does this; we explicitly trigger to keep UX consistent
      // for users who clicked the button right as their session
      // lapsed.
      try {
        window.location.assign('/api/auth/login');
      } catch {
        /* no-op */
      }
      setInFlight(false);
      return;
    }

    if (response.status === 403) {
      toast.error("You don't have System Admin access.");
      setInFlight(false);
      return;
    }

    console.error('[SystemAdminLink] handoff failed', response.status);
    // Surface MRP's `detail` slug to the browser console AND the
    // toast so prod triage gets A/B/C/D attribution without needing
    // DevTools at all. The `detail` field is either:
    //   • a short opaque slug string  (legacy shape)
    //   • an object `{code, gateway_status, gateway_detail}` when MRP
    //     was able to capture Gateway's response shape (new shape for
    //     `gateway_mint_failed` paths — see routes/admin_handoff.py)
    let detail = null;
    let humanLabel = null;
    try {
      const body = await response.json();
      const d = body && body.detail;
      if (typeof d === 'string') {
        detail = d;
        humanLabel = d;
      } else if (d && typeof d === 'object' && typeof d.code === 'string') {
        detail = d.code;
        const parts = [d.code];
        if (d.gateway_status) parts.push(`gw=${d.gateway_status}`);
        if (d.gateway_detail) parts.push(d.gateway_detail);
        humanLabel = parts.join(' · ');
      }
    } catch {
      /* no-op — body may be empty/non-JSON */
    }
    if (detail) {
      console.error('[SystemAdminLink] handoff detail:', humanLabel || detail);
      toast.error(`Could not open System Admin (${humanLabel || detail}).`);
    } else {
      toast.error('Could not open System Admin. Please try again.');
    }
    setInFlight(false);
  };

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          disabled={inFlight}
          data-testid="sidebar-system-admin-button"
          aria-label="System Admin"
          className="group w-14 py-1.5 rounded-lg flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {/* Icon chip matches the NavItem rail: hover paints only the
              tile behind the icon, not the whole button. This entry has
              no active/route state — it's an action — so it only ever
              renders the inactive/hover treatment. */}
          <span className="flex items-center justify-center w-11 h-11 rounded-2xl transition-all duration-200 text-muted-foreground group-hover:bg-secondary group-hover:text-foreground">
            <ShieldCheck className="w-5 h-5" strokeWidth={2} />
          </span>
          <span className="text-[9px] font-medium leading-none text-muted-foreground group-hover:text-foreground transition-colors duration-200">Sys Admin</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        className="shadow-md"
        sideOffset={12}
      >
        System Admin
      </TooltipContent>
    </Tooltip>
  );
}
